import React, { useContext, useEffect, useRef, useState } from "react";
import * as QuickSightEmbedding from "amazon-quicksight-embedding-sdk";
import { Alert, Box, Spinner } from "@amzn/awsui-components-react";
import { useQuery } from "@tanstack/react-query";
import { DashboardApiFactory } from "../../lusca-search-api/LuscaSearchApiFactory";
import DarkModeContext from "../../contexes/DarkModeContext";
import { GAMMA_STAGE, PROD_STAGE } from "../../constants";

interface QuickSightDashboardProps {
  stage: string;
  onLoad?: () => void;
}

const CONTAINER_HEIGHT = "calc(100vh - 60px)";

export const QuickSightDashboard: React.FC<QuickSightDashboardProps> = ({
  stage,
  onLoad,
}) => {
  const containerRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const dashboardExperienceRefs = useRef<{
    [key: string]: QuickSightEmbedding.DashboardExperience | null;
  }>({});
  const embedAttemptedRefs = useRef<{ [key: string]: boolean }>({});
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const darkModeContext = useContext(DarkModeContext);
  const [isDarkTheme, setIsDarkTheme] = useState(darkModeContext);

  // Create persistent containers once so we dont re-embed every time
  useEffect(() => {
    if (mainContainerRef.current) {
      [PROD_STAGE, GAMMA_STAGE].forEach((stageKey) => {
        if (!containerRefs.current[stageKey]) {
          const div = document.createElement("div");
          div.style.width = "100%";
          div.style.height = CONTAINER_HEIGHT;
          div.style.position = "absolute";
          div.style.top = "0";
          div.style.left = "0";
          div.style.display = "none"; // Initially hidden
          containerRefs.current[stageKey] = div;
          mainContainerRef.current!.appendChild(div);
        }
      });
    }
  }, []);

  // Show/hide containers based on current stage
  useEffect(() => {
    Object.keys(containerRefs.current).forEach((stageKey) => {
      const container = containerRefs.current[stageKey];
      if (container) {
        container.style.display = stageKey === stage ? "block" : "none";
      }
    });
  }, [stage]);

  const dashboardApi = DashboardApiFactory();

  const THEMES = {
    DARK:
      stage === GAMMA_STAGE
        ? "arn:aws:quicksight:us-east-1:239039480348:theme/cloudscape"
        : "arn:aws:quicksight:us-east-1:800989574994:theme/e42b2ecd-7436-45a0-a032-52469c99af64",
    LIGHT: "arn:aws:quicksight::aws:theme/CLASSIC",
  };

  const applyTheme = async (newTheme: boolean) => {
    const currentDashboard = dashboardExperienceRefs.current[stage];
    if (currentDashboard) {
      try {
        const setThemePromise = new Promise((resolve, reject) => {
          try {
            const result = currentDashboard.setTheme(
              newTheme ? THEMES.DARK : THEMES.LIGHT
            );
            resolve(result);
          } catch (e) {
            reject(e);
          }
        });

        await Promise.race([
          setThemePromise,
          new Promise((_, reject) => {
            setTimeout(
              () => reject(new Error("Theme change timed out")),
              30000
            );
          }),
        ]);

        setIsDarkTheme(newTheme);
      } catch (err) {
        console.warn(
          "Theme change timed out (this is expected and harmless - dashboard continues to work):",
          err
        );
      }
    } else {
      console.log("Dashboard not ready, setting initial theme state");
      setIsDarkTheme(newTheme);
    }
  };

  useEffect(() => {
    if (darkModeContext !== isDarkTheme) {
      applyTheme(darkModeContext);
    }
  }, [applyTheme, darkModeContext, isDarkTheme]);

  const { data: embedUrlData } = useQuery(
    ["dashboardEmbed", stage],
    async () => {
      const { data } = await dashboardApi.getDashboardEmbedUrl(stage);
      return data;
    },
    {
      enabled: !!stage,
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: false,
    }
  );

  useEffect(() => {
    // If dashboard already exists for this stage, just show it
    if (dashboardExperienceRefs.current[stage]) {
      setIsLoading(false);
      return;
    }

    // Only embed if dashboard has not previously been embedded
    if (
      embedUrlData?.embedUrl &&
      containerRefs.current[stage] &&
      !embedAttemptedRefs.current[stage]
    ) {
      embedAttemptedRefs.current[stage] = true;
      const embedDashboard = async () => {
        try {
          setIsLoading(true);
          const embeddingContext =
            await QuickSightEmbedding.createEmbeddingContext();
          const experience = await embeddingContext.embedDashboard(
            {
              url: embedUrlData.embedUrl,
              container: containerRefs.current[stage]!,
              width: "100%",
              height: "100%",
              resizeHeightOnSizeChangedEvent: true,
              onChange: (event: QuickSightEmbedding.EmbeddingEvents) => {
                if (event.eventName === "FRAME_LOADED") {
                  setIsLoading(false);
                  onLoad?.();
                }
              },
            },
            {
              locale: "en-US",
              sheetOptions: {
                initialSheetId: undefined,
                singleSheet: false,
                emitSizeChangedEventOnSheetChange: true,
              },
              toolbarOptions: {
                export: true,
                undoRedo: true,
                reset: true,
                bookmarks: true,
              },
              themeOptions: {
                themeArn: isDarkTheme ? THEMES.DARK : THEMES.LIGHT,
              },
              attributionOptions: {
                overlayContent: false,
              },
            }
          );
          dashboardExperienceRefs.current[stage] = experience;
          applyTheme(isDarkTheme);
        } catch (err) {
          console.error("Error embedding dashboard for stage:", stage, err);
          setError(
            err instanceof Error ? err : new Error("Failed to embed dashboard")
          );
          setIsLoading(false);
        }
      };

      embedDashboard();
    }
  }, [embedUrlData, stage, onLoad, isDarkTheme]);

  const formatQuickSightError = (err: unknown): string => {
    if (err instanceof Error) {
      try {
        const errorData = JSON.parse(
          err.message.replace("QuickSight Error: ", "")
        );
        return errorData.message?.[0] || err.message;
      } catch {
        return err.message.replace("QuickSight Error: ", "");
      }
    }
    return String(err);
  };

  if (error) {
    return (
      <Box padding="l">
        <Alert
          type="error"
          header="Error loading dashboard"
          dismissible={false}
        >
          {formatQuickSightError(error)}
        </Alert>
      </Box>
    );
  }

  return (
    <Box padding="n">
      {isLoading && (
        <Box margin="xxl" textAlign="center">
          <Spinner size="large" />
        </Box>
      )}
      <div
        ref={mainContainerRef}
        style={{
          width: "100%",
          height: CONTAINER_HEIGHT,
          position: "relative",
          visibility: isLoading ? "hidden" : "visible",
          opacity: isLoading ? 0 : 1,
          transition: "opacity 0.3s ease-in-out",
        }}
      />
    </Box>
  );
};

export default QuickSightDashboard;
